import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { UsageGroupComparisonDTO } from "../../../api/admin/types";
import { adjustWorkflowTotal } from "../shared/constants";

interface WowBarChartProps {
  groups: UsageGroupComparisonDTO[];
  title?: string;
  subtitle?: string;
  showEpic?: boolean;
}

interface WowBarRow {
  label: string;
  delta: number;
  pct: number | null;
  baseline: number;
}

const formatPct = (pct: number | null, baseline: number): string => {
  // Two cases produce a meaningless percent that's worse than no percent:
  // 1. baseline is 0 (BE returns null) - show "new" for greenfield activity
  // 2. baseline is small (<5) - a +/-100% on tiny numbers is noise
  if (pct === null) return baseline === 0 ? "new" : "—";
  if (Math.abs(baseline) < 5) return "—";
  const rounded = Math.round(pct);
  return `${rounded > 0 ? "+" : ""}${rounded}%`;
};

const WowBarChart: React.FC<WowBarChartProps> = ({ groups, title, subtitle, showEpic = true }) => {
  const data: WowBarRow[] = useMemo(() => {
    const rows = groups.map((g) => {
      // When Epic is hidden, recompute baseline / delta / pct against the
      // adjusted human-only totals so the bars match the rest of the page.
      const baseline = adjustWorkflowTotal(g.period_a.total, g.period_a.totals, showEpic);
      const current = adjustWorkflowTotal(g.period_b.total, g.period_b.totals, showEpic);
      const delta = current - baseline;
      let pct: number | null;
      if (baseline === 0 && current === 0) pct = 0;
      else if (baseline === 0) pct = null;
      else pct = Math.round(((current - baseline) / baseline) * 10000) / 100;
      return { label: g.label, delta, pct, baseline };
    });
    return rows
      .filter((r) => r.delta !== 0)
      .sort((a, b) => a.delta - b.delta);
  }, [groups, showEpic]);

  const height = Math.max(200, data.length * 22 + 40);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 20,
        width: "100%",
        maxWidth: 1200,
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {title ?? "Period-over-period task change"}
        </div>
        {subtitle ? <div style={{ fontSize: 12, color: "#6b7280" }}>{subtitle}</div> : null}
      </div>

      {data.length === 0 ? (
        <div style={{ color: "#6b7280", padding: 24, textAlign: "center" }}>
          No agencies with non-zero change in the selected periods.
        </div>
      ) : (
        <div style={{ width: "100%", height }}>
          <ResponsiveContainer>
            <BarChart data={data} layout="vertical" margin={{ top: 8, right: 32, left: 12, bottom: 8 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis
                type="category"
                dataKey="label"
                width={160}
                tick={{ fontSize: 11 }}
                interval={0}
              />
              <Tooltip
                formatter={(_value, _name, item) => {
                  // Override the default tooltip body so the user sees the
                  // baseline + pct alongside the raw delta.
                  const row = item.payload as WowBarRow;
                  const pct = formatPct(row.pct, row.baseline);
                  const sign = row.delta > 0 ? "+" : "";
                  return [`${sign}${row.delta} (${pct} vs ${row.baseline})`, "Change"];
                }}
              />
              <ReferenceLine x={0} stroke="#9ca3af" />
              <Bar dataKey="delta" isAnimationActive={false}>
                {data.map((entry, idx) => (
                  <Cell
                    key={idx}
                    fill={entry.delta < 0 ? "#e57373" : "#66bb6a"}
                  />
                ))}
                <LabelList
                  dataKey="delta"
                  position="right"
                  content={(props: {
                    x?: string | number;
                    y?: string | number;
                    width?: string | number;
                    height?: string | number;
                    index?: number;
                  }) => {
                    // Custom content lets us pull pct/baseline from the row
                    // by index - using a formatter+find on `value` would
                    // collide whenever two agencies share the same delta.
                    const { x, y, width, height, index } = props;
                    if (index === undefined) return null;
                    const row = data[index];
                    if (!row) return null;
                    const xN = Number(x ?? 0);
                    const yN = Number(y ?? 0);
                    const wN = Number(width ?? 0);
                    const hN = Number(height ?? 0);
                    // Negative bars grow leftward, so width is negative and
                    // the label belongs on the LEFT of the bar's start.
                    const isNeg = row.delta < 0;
                    const labelX = isNeg ? xN + wN - 6 : xN + wN + 6;
                    const labelY = yN + hN / 2 + 4;
                    const pct = formatPct(row.pct, row.baseline);
                    const sign = row.delta > 0 ? "+" : "";
                    return (
                      <text
                        x={labelX}
                        y={labelY}
                        textAnchor={isNeg ? "end" : "start"}
                        fontSize={11}
                        fontWeight={600}
                        fill="#374151"
                      >
                        {`${sign}${row.delta} (${pct})`}
                      </text>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default WowBarChart;
